import { EQUIPMENT_APPROVE_REVIEW, EQUIPMENT_REJECT_REVIEW } from "../../../../gql/landingapi";
import { EQUIPMENT_REVIEW_ACTIONS, GROUPS_TOKEN_PATH, equipmentStatus } from "../../../../constants";
import { Notify, NotifyPropsType, getFieldValue } from "@digitallab/grid-common-frontend-components";
import { ApolloClient, ApolloQueryResult } from "@apollo/client";
import { Auth } from "aws-amplify";
import { RowNode } from "ag-grid-community";
import { get } from "lodash";

export enum REVIEW_TYPES {
  CREATE,
  UPDATE,
  DELETE,
  RESTORE,
}

export enum DECISIONS {
  REVERT = "REVERT",
  REJECT = "REJECT",
  APPROVE = "APPROVE",
  REVIEW = "REVIEW",
}

export interface ProposedChangesResponse {
  approveChange: {
    status: string;
  };
}

const checkReviewers = (dataItem: any, userId: string) => {
  return dataItem?.reviewers?.includes(userId);
}

// Cognito groups of the signed-in user, read from the same token claim the backend checks.
// null when the lookup itself failed, so a caller can tell "could not check" apart from
// "checked, and the user has no groups".
export const getCurrentUserGroups = async (): Promise<string[] | null> => {
  try {
    const currentAuthenticatedUser = await Auth.currentAuthenticatedUser();
    return get(currentAuthenticatedUser, GROUPS_TOKEN_PATH) ?? [];
  } catch (error) {
    console.error("Unable to read the user groups: ", (error as Error).message);
    return null;
  }
};

export const isRowReviewable = (rowData: RowNode, userId: string): boolean => {
  return getFieldValue(rowData.data, "status") === equipmentStatus.pending.key &&
    checkReviewers(rowData.data, userId);
}

export const isReviewClosed = (reviewStatus?: string | null): boolean =>
  Boolean(reviewStatus) && reviewStatus !== equipmentStatus.pending.key;

export const simpleRejectProposedChanges = async (
  client: ApolloClient,
  reviewId: string,
  reason: string,
  comment: string | null = "",
  successMessage = "Change(s) reverted due to rejection and the workflow status is \"Active\" again.",
): Promise<boolean> => {
  const notification = await rejectProposedChanges(
    client,
    "not-used",
    {
      userId: "not-used",
      reviewId,
      reason,
      comment,
      decisionType: DECISIONS.REJECT,
      reviewType: REVIEW_TYPES.UPDATE
    } as DecisionData,
    {
      type: "error",
      icon: "alarm",
      title: "",
      text: successMessage
    }
  );
  if (notification.type === "success") {
    notification.text = successMessage;
  }
  Notify(notification);
  return notification.type === "success";
}

export interface DecisionData {
  userId: string,
  reviewId: string,
  reason: string,
  comment: string,
  decisionType: DECISIONS,
  reviewType: REVIEW_TYPES,
  shortId?: string,
  configurationBaseline?: string,
}

export const triggerReauthentication = async (
  decisionsData: DecisionData[],
) => {
  localStorage.setItem(
    "reviewReauthenticationData",
    JSON.stringify(decisionsData)
  );
  await Auth.signOut({
    global: true
  });
}

export const getSuccessMessage = (action: REVIEW_TYPES) => {
  switch (action) {
    case REVIEW_TYPES.CREATE:
      return "New equipment has been reviewed and approved successfully!";
    case REVIEW_TYPES.DELETE:
      return "Request to remove equipment has been approved, equipment is deleted.";
    case REVIEW_TYPES.RESTORE:
      return 'Request to restore equipment has been approved, workflow status is "Active" again.';
    case REVIEW_TYPES.UPDATE:
      return "Equipment details are reviewed and approved successfully.";
    default:
      return "SUCCESS!";
  }
};

export const approveProposedChanges = async (
  client: ApolloClient,
  userId: string,
  decision: DecisionData,
  notification: NotifyPropsType,
): Promise<NotifyPropsType> => {
  if (decision.userId.toLowerCase() === userId.toLowerCase()) {
    const result = await client.mutate({
      mutation: EQUIPMENT_APPROVE_REVIEW,
      variables: {
        reviewIds: [decision.reviewId],
        editReason: decision.reason,
        editComment: decision.comment,
        configurationBaseline: decision.configurationBaseline
      },
      fetchPolicy: "no-cache"
    }) as ApolloQueryResult<ProposedChangesResponse>;
    if (!result?.error && EQUIPMENT_REVIEW_ACTIONS.success.key === result.data.approveChange.status) {
      notification.type = "success";
      notification.icon = "circle_confirm";
      notification.title = "";
      notification.text = getSuccessMessage(decision.reviewType);
    } else if (EQUIPMENT_REVIEW_ACTIONS.duplicate.key === result.data.approveChange.status) {
      notification.type = "error";
      notification.icon = "alarm";
      notification.title = "Duplicate detected";
      notification.text = "Please investigate or reach out to an Administrator";
    } else if (EQUIPMENT_REVIEW_ACTIONS.missingConfigurationBaseline.key === result.data.approveChange.status) {
      notification.type = "error";
      notification.icon = "alarm";
      notification.title = "Configuration Baseline required";
      notification.text = "This change requires a Configuration Baseline before it can be approved.";
    } else if (EQUIPMENT_REVIEW_ACTIONS.notConfigurationManager.key === result.data.approveChange.status) {
      notification.type = "error";
      notification.icon = "alarm";
      notification.title = "You cannot approve equipment";
      notification.text = "Only a Configuration Manager may approve this change.";
    }
  } else {
    notification.type = "error";
    notification.icon = "alarm";
    notification.title = "You cannot approve equipment";
    notification.text = "You are not selected as the reviewer";
  }
  return notification;
};

export const rejectProposedChanges = async (
  client: ApolloClient,
  userId: string,
  decision: DecisionData,
  notification: NotifyPropsType
): Promise<NotifyPropsType> => {
  if (decision.userId.toLowerCase() === userId.toLowerCase()) {
    try {
      const result = await client.mutate({
        mutation: EQUIPMENT_REJECT_REVIEW,
        variables: {
          reviewIds: [decision.reviewId],
          editReason: decision.reason,
          editComment: decision.comment
        },
        fetchPolicy: "no-cache"
      }) as ApolloQueryResult<ProposedChangesResponse>;
      if (!result?.error) {
        if (result?.data?.approveChange?.status === EQUIPMENT_REVIEW_ACTIONS.failure.key) {
          notification.type = "error";
          notification.icon = "alarm";
          notification.title = "";
          notification.text = "Rejection failed. Please try again.";
        } else {
          notification.type = "success";
          notification.icon = "circle_confirm";
          notification.title = "";
          notification.text = "Change(s) reverted due to rejection and the workflow status is \"Active\" again.";
        }
      }
    } catch (error) {
      notification.type = "error";
      notification.icon = "alarm";
      notification.title = "";
      notification.text = "Unhandled error occurred. Please try again.";
    }
  } else {
    notification.type = "error";
    notification.icon = "alarm";
    notification.title = "You cannot reject equipment";
    notification.text = "You are not selected as the reviewer";
  }
  return notification;
};

export const handleReauthentication = async (
  client: ApolloClient,
  userId: string,
  setLoading: (loading: boolean) => void,
) => {
  const reviewReauthenticationData = localStorage.getItem("reviewReauthenticationData");
  const notifications = [] as NotifyPropsType[];
  if (reviewReauthenticationData) {
    const decisions: DecisionData[] = JSON.parse(reviewReauthenticationData || "[]") ?? [];
    await Promise.all(
      decisions.map(async (decision: DecisionData) => {
        let notification = {
          type: "error",
          icon: "alarm",
          title: "Unable to approve equipment at this moment",
          text: "Please try again."
        } as NotifyPropsType;
        try {
          switch (decision.decisionType) {
            case DECISIONS.APPROVE:
              notification = await approveProposedChanges(client, userId, decision, notification);
              break;
            case DECISIONS.REJECT:
            default:
              notification = await rejectProposedChanges(client, userId, decision, notification);
              break;
          }
        } catch (error) {
          console.log("ReAuthenticationHandler error: ", (error as Error).message);
        } finally {
          let title = notification.title;
          if (decision.shortId) {
            title = `${decision.shortId}: ${title}`;
          }
          notifications.push({
            ...notification,
            title
          });
        }
      })
    );
    notifications.forEach((notification) => Notify(notification));
  }
  localStorage.removeItem("reviewReauthenticationData");
  setLoading(false);
};
